import { useState } from "react";
import { Copy, KeyRound } from "lucide-react";
import { api, formatDateTime } from "../api";
import { useApi } from "../hooks";
import { ErrorState, LoadingState } from "./Ui";
import "../polish/agent-keys.css";

type Key = { id: number; name: string; key_hint: string; last_used_at: string | null; uses: number; created_at: string };

/**
 * Settings > Agent API: personal keys that let your own AI assistant (Claude, ChatGPT) read
 * NOLA through the MCP server at /api/mcp, as you and read only. A new key is shown once.
 */
export default function AgentKeys() {
  const list = useApi<{ keys: Key[] }>("/api/agent-keys");
  const [name, setName] = useState("");
  const [made, setMade] = useState<{ name: string; key: string } | null>(null);
  const [busy, setBusy] = useState("");
  // Revoking stops whatever uses the key at once, so it asks first.
  const [confirming, setConfirming] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const endpoint = `${window.location.origin}/api/mcp`;

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy("create"); setNotice(null);
    try {
      const r = await api<{ name: string; key: string }>("/api/agent-keys", { method: "POST", body: JSON.stringify({ name }) });
      setMade({ name: r.name, key: r.key }); setName(""); list.reload();
    } catch (err) { setNotice({ ok: false, text: err instanceof Error ? err.message : "The key could not be made." }); }
    finally { setBusy(""); }
  }
  async function revoke(k: Key) {
    setBusy(`r${k.id}`); setNotice(null);
    try { await api(`/api/agent-keys/${k.id}`, { method: "DELETE" }); setConfirming(null); setNotice({ ok: true, text: `Revoked "${k.name}". Anything using it stops working now.` }); list.reload(); }
    catch (err) { setNotice({ ok: false, text: err instanceof Error ? err.message : "The key could not be revoked." }); }
    finally { setBusy(""); }
  }
  async function copy(text: string, what: string) {
    try { await navigator.clipboard.writeText(text); setNotice({ ok: true, text: `${what} copied.` }); }
    catch { setNotice({ ok: false, text: "Copy did not work here. Select the text and copy it." }); }
  }
  const config = made ? JSON.stringify({ mcpServers: { nola: { command: "npx", args: ["-y", "mcp-remote@0.1.29", endpoint, "--header", `Authorization:Bearer ${made.key}`] } } }, null, 2) : "";

  return (
    <div className="agent-keys">
      <section className="panel">
        <div className="panel-heading"><div><h2>What this does</h2>
          <p className="agent-note">An agent key lets your own Claude or ChatGPT read NOLA for you: clients, ads, LSA calls, SEO, tasks and reports, with the same access you have. It can only read. It cannot see settings, keys, logins or call recordings, and it cannot change anything.</p></div></div>
        <dl className="agent-facts">
          <div><dt>Server address</dt><dd><code>{endpoint}</code> <button type="button" className="text-button" onClick={() => void copy(endpoint, "Address")}>Copy</button></dd></div>
          <div><dt>How it signs in</dt><dd>Header <code>Authorization: Bearer nola_…</code> with your key</dd></div>
        </dl>
      </section>

      {made && (
        <section className="panel agent-made" aria-live="polite">
          <div className="panel-heading"><div><h2>Your new key: {made.name}</h2><p className="agent-note">Copy it now. NOLA keeps only a fingerprint, so it cannot show this key again.</p></div></div>
          <div className="agent-secret"><code>{made.key}</code><button type="button" className="button secondary" onClick={() => void copy(made.key, "Key")}><Copy aria-hidden="true" />Copy key</button></div>
          <div className="agent-howto">
            <h3>Claude Code</h3>
            <pre>{`claude mcp add --transport http nola ${endpoint} --header "Authorization: Bearer ${made.key}"`}</pre>
            <h3>Claude Desktop</h3>
            <p className="agent-note">Settings, Developer, Edit config, then add this and restart Claude:</p>
            <pre>{config}</pre>
            <button type="button" className="text-button" onClick={() => void copy(config, "Desktop config")}>Copy the Desktop config</button>
          </div>
          <div><button type="button" className="button secondary" onClick={() => setMade(null)}>I copied it</button></div>
        </section>
      )}

      {notice && <p className={`inline-notice ${notice.ok ? "success" : "danger"}`} role={notice.ok ? "status" : "alert"}>{notice.text}</p>}

      <section className="panel">
        <div className="panel-heading"><div><h2>Your keys</h2></div></div>
        <form className="agent-create" onSubmit={create}>
          <label><span className="field-label">Name it after where it is used</span><input id="agent-key-name" value={name} maxLength={80} placeholder="Claude on my laptop" onChange={(e) => setName(e.target.value)} /></label>
          <button type="submit" className="button primary" disabled={busy === "create" || !name.trim()}><KeyRound aria-hidden="true" />{busy === "create" ? "Making…" : "Make a key"}</button>
        </form>
        {list.loading && !list.data ? <LoadingState label="Loading your keys" />
          : list.error ? <ErrorState message={list.error} onRetry={list.reload} />
            : !list.data?.keys.length ? <p className="agent-empty">No keys yet.</p>
              : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Name</th><th>Ends in</th><th>Last used</th><th className="num">Reads</th><th>Made</th><th><span className="sr-only">Actions</span></th></tr></thead>
                    <tbody>{list.data.keys.map((k) => (
                      <tr key={k.id}>
                        <td data-label="Name">{k.name}</td>
                        <td data-label="Ends in"><code>…{k.key_hint}</code></td>
                        <td data-label="Last used">{k.last_used_at ? formatDateTime(k.last_used_at) : "Never"}</td>
                        <td className="num" data-label="Reads">{k.uses.toLocaleString()}</td>
                        <td data-label="Made">{formatDateTime(k.created_at)}</td>
                        <td>{confirming === k.id
                          ? <span className="agent-confirm">Stop it now? <button type="button" className="text-button" disabled={!!busy} onClick={() => void revoke(k)}>{busy === `r${k.id}` ? "Revoking…" : "Revoke"}</button><button type="button" className="text-button" onClick={() => setConfirming(null)}>Keep</button></span>
                          : <button type="button" className="text-button" disabled={!!busy} onClick={() => setConfirming(k.id)}>Revoke</button>}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
      </section>
    </div>
  );
}
